import { React, PropTypes, reduxConnect, css, styled } from '../lib/imports';

import {
  ColorProvider,
  Spacing,
  ShadowBox,
  Text,
  TextBar,
  Divider,
  PickItem,
  Image,
  HeatStreakIcon,
  NotificationCard,
} from '../components';
import { useCdnImage, getAssetSet } from '../app/context/config';
import { isDateYesterday, isDateToday, getNextDay, getPreviousDay, getMonthDayDate, getDayDate } from '../lib/date';
import { slotItemBuilder } from '../picks/utils';

const assetSet = (filename) => getAssetSet(useCdnImage, filename);

const containerBaseStyles = ({ theme }) => css`
  padding-top: ${theme.spacings.exa};
`;

const Container = styled('div')(containerBaseStyles);

const streakItemBaseStyles = () => css`
  display: flex;
`;

const StreakItem = styled('div')(streakItemBaseStyles);

const heatLegendBaseStyles = () => css`
  position: relative;
`;

const HeatLegend = styled('div')(heatLegendBaseStyles);

const heatLineBaseStyles = ({ theme, variant }) => css`
  background: black;
  position: absolute;
  top: 0;
  left: 15px;
  width: 2px;
  height: 100%;

  background: linear-gradient(
    360deg,
    ${theme.colors.heatStreak[variant].start} 2%,
    ${theme.colors.heatStreak[variant].end} 47%
  );
`;

const HeatLine = styled('div')(heatLineBaseStyles);

const pickListBaseStyles = () => css`
  position: relative;
  width: 100%;

  span span {
    font-weight: bold;
  }
`;

const PickList = styled('div')(pickListBaseStyles);

const heatItemBaseStyles = () => css`
  width: 100%;
`;

const HeatItem = styled('div')(heatItemBaseStyles);

const heatDateBaseStyles = ({ theme }) => css`
  position: absolute;
  top: -25px;
  left: 15px;
  display: flex;
  justify-content: space-between;
  align-items: self-end;
  width: 100%;
  padding-right: ${theme.spacings.mega};
`;

const HeatDate = styled('div')(heatDateBaseStyles);

const heatDateTextBaseStyles = () => css``;

const HeatDateText = styled('div')(heatDateTextBaseStyles);

const heatDateStatusBaseStyles = ({ theme }) => css`
  border-radius: ${theme.borderRadius.giga};
  background-color: ${theme.colors.n900};
  padding: 1px ${theme.spacings.byte} 0;
`;

const HeatDateStatus = styled('div')(heatDateStatusBaseStyles);
const Loyalty3NotificationDate = '2023-02-21';

const imageContainerBaseStyles = () => css`
  position: relative;
  z-index: 999;
  width: 32px;
  height: 32px;
`;

const ImageContainer = styled('div')(imageContainerBaseStyles);

const HeatStreak = ({
  streakList,
  noPicksText,
  gameStatusText,
  todayNoPicksSubText,
  resultsPendingText,
  noPicksAggregatedText,
  loyalty3NotificationTitle,
  loyalty3NotificationText,
  byPlayerId,
  byTeamId,
  gamesInProgress,
  noPicksAggregated,
  yourPicksText,
}) => {
  const assetList = assetSet(['loadingSticks', 'historyUnscored']);

  const slotBuilder = slotItemBuilder({
    playerData: byPlayerId,
    teamData: byTeamId,
  });

  let startAggregatedContest = false;
  let isCurrentDateAggregated = false;
  let aggregateDateRangeText = null;

  return (
    <Container>
      <Spacing sizeLeft='mega' sizeRight='mega'>
        {streakList.map(
          ({
            key,
            contestDate,
            hasPicks,
            hasLoyalty3Notification,
            isUnscored,
            dateText,
            isToday,
            heatLineStreak,
            streak,
            streakText,
            streakVariant,
            picks,
            pickStatustext,
            statusBgColor,
          }) => {
            const aggregatedDateRange = noPicksAggregated[contestDate];
            const isDateAggregated = !!aggregatedDateRange;
            let aggregatedDateCount = 0;

            if (!startAggregatedContest && isDateAggregated) {
              startAggregatedContest = aggregatedDateRange;
              isCurrentDateAggregated = true;
            }

            if (startAggregatedContest) {
              const aggregateDatesForCurrentDate = startAggregatedContest[contestDate];

              if (aggregateDatesForCurrentDate) {
                const aggregatedDateKeys = Object.keys(startAggregatedContest);
                aggregatedDateCount = aggregatedDateKeys.length - 1;
                const startAggregatedDate = aggregatedDateKeys[0];

                const endAggregatedDate = aggregatedDateKeys[aggregatedDateCount];

                aggregateDateRangeText = `${getMonthDayDate(endAggregatedDate)}-${getDayDate(startAggregatedDate)}`;

                if (contestDate === endAggregatedDate) {
                  isCurrentDateAggregated = false;
                }
              } else {
                startAggregatedContest = false;
              }
            }

            const contestDateText = startAggregatedContest ? aggregateDateRangeText : dateText;

            const contestNoPickText = startAggregatedContest ? noPicksAggregatedText(aggregatedDateCount) : noPicksText;

            return isCurrentDateAggregated ? null : (
              <Spacing key={key}>
                <StreakItem>
                  <HeatLegend>
                    <Spacing sizeRight='bit'>
                      <HeatLine variant={heatLineStreak} />
                      {!isUnscored ? (
                        <HeatStreakIcon
                          size='bit'
                          variant={streakVariant}
                          assetSrc={assetList.loadingSticks}
                          text={streakText}
                        />
                      ) : (
                        <ImageContainer>
                          <Image src={assetList.historyUnscored} />
                        </ImageContainer>
                      )}
                    </Spacing>
                  </HeatLegend>
                  <PickList>
                    <HeatDate>
                      <HeatDateText>
                        <ColorProvider variant='darkGrey'>
                          <Text size='kilo'>{contestDateText}</Text>
                        </ColorProvider>
                      </HeatDateText>
                      {isToday && gamesInProgress && (
                        <HeatDateStatus>
                          <ColorProvider variant='white'>
                            <Text size='bit' upper bold noMargin>
                              {gameStatusText}
                            </Text>
                          </ColorProvider>
                        </HeatDateStatus>
                      )}
                    </HeatDate>
                    <Spacing sizeLeft='mega' sizeBottom={isToday ? 'peta' : 'exa'}>
                      <ShadowBox centered={!hasPicks} active={hasPicks && picks[0].correct} arrow>
                        {hasPicks && <TextBar variant={statusBgColor} text={yourPicksText} />}
                        {hasPicks &&
                          picks.map(({ playerId, correct = false }, index) => {
                            const { text, subText, assetUrl, secondaryAssetUrl } = slotBuilder(playerId);

                            const correctIcon = correct ? 'success-lite' : '';
                            const icon = isToday ? 'lock' : correctIcon;

                            return (
                              <HeatItem key={`${key}-${playerId}`}>
                                <PickItem
                                  variant='leftSelected'
                                  assetUrl={assetUrl}
                                  secondaryAssetUrl={secondaryAssetUrl}
                                  text={text}
                                  subText={subText}
                                  isActive={correct}
                                  hasIcon={isToday || correct}
                                  correct={correct}
                                  isToday={isToday}
                                  icon={icon}
                                />
                                {index !== 2 && <Divider />}
                              </HeatItem>
                            );
                          })}

                        {hasPicks && !isToday && pickStatustext && !isUnscored && (
                          <TextBar variant={statusBgColor} text={pickStatustext} />
                        )}

                        {isUnscored && <TextBar variant={statusBgColor} text={resultsPendingText} />}

                        {!hasPicks &&
                          isToday &&
                          [0, 1, 2].map((number) => (
                            <HeatItem key={`placeholder-${number}`}>
                              <PickItem
                                variant='placeholder'
                                assetUrl={assetList.loadingSticks}
                                subText={todayNoPicksSubText}
                                assetHasPadding={isToday && !hasPicks}
                                hasIcon={false}
                              />
                              {number !== 2 && <Divider />}
                            </HeatItem>
                          ))}

                        {!hasPicks && !isToday && (
                          <Spacing sizeLeft='mega' paddingTop='bit'>
                            <Text as='span' size='mega' noMargin>
                              {contestNoPickText}
                            </Text>
                          </Spacing>
                        )}
                      </ShadowBox>
                      {hasLoyalty3Notification && (
                        <Spacing paddingTop='giga'>
                          <NotificationCard titleText={loyalty3NotificationTitle} bodyText={loyalty3NotificationText} />
                        </Spacing>
                      )}
                    </Spacing>
                  </PickList>
                </StreakItem>
              </Spacing>
            );
          },
        )}
      </Spacing>
    </Container>
  );
};

HeatStreak.propTypes = {
  streakList: PropTypes.array,
  noPicksText: PropTypes.string,
  todayNoPicksSubText: PropTypes.string,
  gameStatusText: PropTypes.string,
  resultsPendingText: PropTypes.string,
  noPicksAggregatedText: PropTypes.func,
  byPlayerId: PropTypes.object,
  byTeamId: PropTypes.object,
  gamesInProgress: PropTypes.bool,
  noPicksAggregated: PropTypes.object,
  platform: PropTypes.string,
  language: PropTypes.string,
};

const mapStateToProps = (state) => {
  const { config, strings, heatStreak, games } = state;
  const { noGamesStarted, allGamesStarted } = games;
  const { byDate, byPlayerId, byTeamId } = heatStreak;

  const isFrench = config.language === 'fr';

  const allStrings = {
    title: strings['titles/section/howToPlay/text'],
    noPicksText: strings['history/noPicks/text'],
    gameStatusText: strings['history/inProgress/text'],
    todayNoPicksSubText: strings['history/noPicks/title'],
    resultsPendingText: strings['history/resultsPending/text'],
    noPicksAggregatedText: strings['history/noPicksAggregated/text'],
    loyalty3NotificationTitle: strings['history/notification/loyalty3/title'],
    loyalty3NotificationText: strings['history/notification/loyalty3/text'],
    yourPicksText: strings['titles/section/yourPicks/text'],
  };

  const statusStrings = {
    1: strings['heatstreak/correct/text'],
    2: strings['heatstreak/incorrect/text'],
    3: strings['heatstreak/noPicks/text'],
    4: strings['heatstreak/offers/text'],
  };

  const streakList = Object.keys(byDate).map((id, index) => {
    const date = byDate[id];
    const { month, day, points, streak, offers } = date;

    const eventDate = isFrench
      ? `${day} ${strings[`dates/month/${month}/text`]} `
      : `${strings[`dates/month/${month}/text`]} ${day}`;
    const noPicksText = statusStrings[3];

    const hasPicks = !!date.picks.length;
    const hasLoyalty3Notification = date.contestDate === Loyalty3NotificationDate;
    const isToday = isDateToday(date.contestDate);
    const isYesterday = isDateYesterday(date.contestDate);
    const isUnscored = isYesterday && date.picks[0] && !Object.prototype.hasOwnProperty.call(date.picks[0], 'correct');

    const todayDateText = isToday && strings['dates/today/text'];
    const yesterdayDateText = isYesterday && strings['dates/yesterday/text'];
    const dateText = todayDateText || yesterdayDateText || eventDate;
    const pickStatustext =
      points !== 0 ? statusStrings[1](points) : offers.length !== 0 ? statusStrings[4] : statusStrings[2];

    const todayVariantWithPicks = isToday && hasPicks && 'locked';
    const todayVariantWithoutPicks = isToday && !hasPicks && 'image';

    const unscoredHeatlineColor = isUnscored && 'h0';
    const coldHeatlineColor = Object.prototype.hasOwnProperty.call(date, 'streak') && !streak && 'cold';
    const streakHeatlineColor = streak && 'h4';
    const heatLineStreak = unscoredHeatlineColor || coldHeatlineColor || streakHeatlineColor || 'h0';

    const otherVariant = streak ? 'hot' : 'cold';
    const streakVariant = todayVariantWithPicks || todayVariantWithoutPicks || otherVariant;

    const pointScoreColor = (points !== 0 || offers.length !== 0) && 'bl900';
    const unscoredColor = isUnscored && 'n900';
    const statusBgColor = pointScoreColor || unscoredColor || 'r500';
    const yourPicksText = allStrings.yourPicksText;
    return {
      ...date,
      key: `streak-${index}-${month}-${day}`,
      hasPicks,
      hasLoyalty3Notification,
      dateText,
      heatLineStreak,
      streak,
      streakVariant,
      streakText: streak && date.streak,
      statusBgColor,
      isToday,
      isYesterday,
      isUnscored,
      pickStatustext,
      yourPicksText,
      noPicksText,
    };
  });

  const noPickDateList = Object.keys(byDate)
    .filter((id) => !byDate[id].picks.length)
    .reduce((acc, curr) => {
      return {
        ...acc,
        [curr]: true,
      };
    }, {});

  const noPicksAggregated = {};
  const noPickDateListArr = Object.keys(noPickDateList);
  let startAggregateDate = null;

  // Dont aggregate the notification date
  const dateIndex = noPickDateListArr.indexOf(Loyalty3NotificationDate);
  if (dateIndex > -1) {
    noPickDateListArr.splice(dateIndex, 1);
  }

  for (let i = 0; i < noPickDateListArr.length; i++) {
    const currentDate = noPickDateListArr[i];
    const nextDate = noPickDateListArr[i - 1];
    const prevDate = noPickDateListArr[i + 1];

    const currentMinusOne = getPreviousDay(currentDate);
    const currentPlusOne = getNextDay(currentDate);

    if (!startAggregateDate && currentMinusOne === prevDate && currentPlusOne === nextDate) {
      startAggregateDate = nextDate;
    }

    if (startAggregateDate) {
      noPicksAggregated[startAggregateDate] = {
        ...noPicksAggregated[startAggregateDate],
        [nextDate]: true,
      };

      if (currentPlusOne !== nextDate) {
        startAggregateDate = null;
      }
    }
  }

  const gamesInProgress = !noGamesStarted && !allGamesStarted;

  return {
    ...allStrings,
    byPlayerId,
    byTeamId,
    streakList,
    gamesInProgress,
    noPicksAggregated,
  };
};

export default reduxConnect(mapStateToProps)(HeatStreak);
